import React, { useState, JSX } from 'react';
import {
  Button,
  CardSubtitle,
  CardText,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row
} from 'reactstrap';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ApiAgent, ApiCommunicationDetails } from '@ibe/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import fallback from '../../../_Translations/generated/agent-profile-information.en.t.json';
import Keys from '../../../_Translations/generated/agent-profile-information.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';

interface Props {
  agent: ApiAgent;
  onSubmit: (communicationDetails: ApiCommunicationDetails) => void;
}

const AgentProfileInformationForm = (props: Props): JSX.Element => {
  const { agent, onSubmit } = props;
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation('agent-profile-information', fallback, Keys);
  const schema = yup.object().shape({
    phone: yup.string().required(t(Keys.requiredField)),
    mobilePhone: yup.string().required(t(Keys.requiredField))
  });
  const defaultValues = {
    phone: agent.communicationDetails?.phone || '',
    mobilePhone: agent.communicationDetails?.mobilePhone || ''
  };

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { ...defaultValues }
  });

  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <Form
      className="mb-5"
      onSubmit={handleSubmit((communicationDetails: ApiCommunicationDetails): void => {
        onSubmit(communicationDetails);
        setIsEditing(false);
      })}
    >
      <Row>
        <Col>
          <FormGroup>
            <CardSubtitle tag="h6" className="font-weight-light">
              {t(Keys.email)}
            </CardSubtitle>
            <CardText>{agent.communicationDetails?.email}</CardText>
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <CardSubtitle tag="h6" className="font-weight-light">
              {t(Keys.username)}
            </CardSubtitle>
            <CardText>{agent.userName}</CardText>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label className="font-weight-light" htmlFor="phone">
              {t(Keys.phone)}
            </Label>
            <Input
              type="text"
              id="phone"
              placeholder={t(Keys.phone)}
              disabled={!isEditing}
              invalid={!!errors.phone}
              {...register('phone')}
              innerRef={register('phone').ref}
              onChange={(e): void => setValue('phone', e.target.value)}
              autoComplete={'tel'}
            />
            {errors.phone && (
              <FormFeedback>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {errors.phone.message}
              </FormFeedback>
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label className="font-weight-light" htmlFor="mobilePhone">
              {t(Keys.mobilePhone)}
            </Label>
            <Input
              type="text"
              id="mobilePhone"
              placeholder={t(Keys.mobilePhone)}
              disabled={!isEditing}
              invalid={!!errors.mobilePhone}
              {...register('mobilePhone')}
              innerRef={register('mobilePhone').ref}
              onChange={(e): void => setValue('mobilePhone', e.target.value)}
              autoComplete={'tel-mobile'}
            />
            {errors.mobilePhone && (
              <FormFeedback>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {errors.mobilePhone.message}
              </FormFeedback>
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          {!isEditing && (
            <Button color="primary" onClick={(): void => setIsEditing(true)}>
              {t(Keys.edit)}
            </Button>
          )}
          {isEditing && (
            <>
              <Button
                color="secondary"
                onClick={(): void => {
                  setIsEditing(false);
                  reset({ ...defaultValues });
                }}
              >
                {t(Keys.cancel)}
              </Button>
              <Button className="ml-3" color="primary" type="submit">
                {t(Keys.save)}
              </Button>
            </>
          )}
        </Col>
      </Row>
    </Form>
  );
};
export default AgentProfileInformationForm;
